import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { BookOpen, ShoppingCart, Books, SignOut, UserCircle, Shield, Compass, Trophy, List } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/289f5bfb-a361-4a61-8407-48b3421a1ec8/images/c4cf84e1360572fb8c7750cbf1af0a64fd151ace8ea145fab45add9f505baa69.jpeg";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/catalog", label: "Catalog", icon: Books, testid: "catalog" },
    { to: "/quiz", label: "Find My Level", icon: Compass, testid: "quiz" },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "leaderboard" },
    ...(user ? [{ to: "/library", label: "Library", icon: BookOpen, testid: "library" }] : []),
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: Shield, testid: "admin", admin: true }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b-2 border-slate-900/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="nav-logo">
          <span className="grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-white shadow-sm ring-1 ring-slate-900/5 overflow-hidden transition-transform group-hover:-rotate-6">
            <img src={LOGO} alt="Reading Keeper bookshelf logo" className="w-full h-full object-cover" />
          </span>
          <span className="font-heading font-bold text-lg sm:text-xl text-ink whitespace-nowrap">Reading Keeper</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1 lg:gap-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.testid}`}>
              <Button variant="ghost" className={`rounded-full font-body font-semibold hover:bg-brand-sky/10 ${l.admin ? "text-brand-grape hover:bg-brand-grape/10" : "text-ink"}`}>
                <l.icon size={20} weight="duotone" className="mr-1" /> {l.label}
              </Button>
            </Link>
          ))}
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-1 shrink-0">
          <Link to="/cart" className="relative" data-testid="nav-cart">
            <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-coral/10">
              <ShoppingCart size={22} weight="duotone" className="text-ink" />
            </Button>
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 grid place-items-center min-w-5 h-5 px-1 rounded-full bg-brand-coral text-white text-xs font-bold" data-testid="cart-count">
                {items.length}
              </span>
            )}
          </Link>

          {/* Desktop auth */}
          <div className="hidden md:flex items-center gap-2 pl-1">
            {user ? (
              <>
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border-2 border-brand-sky/40" />
                ) : (
                  <UserCircle size={34} weight="duotone" className="text-brand-sky" />
                )}
                <Button onClick={logout} variant="ghost" size="icon" className="rounded-full hover:bg-slate-100" data-testid="logout-btn" title="Log out">
                  <SignOut size={20} weight="duotone" className="text-ink-soft" />
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate("/login")} className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold px-5 transition-transform hover:-translate-y-0.5" data-testid="nav-login-btn">
                Sign in
              </Button>
            )}
          </div>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-sky/10" data-testid="mobile-menu-btn" aria-label="Open menu">
                <List size={26} weight="bold" className="text-ink" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82%] max-w-xs p-0 flex flex-col">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex items-center gap-2.5 px-6 py-5 border-b border-slate-900/5">
                <span className="grid place-items-center w-10 h-10 rounded-2xl bg-white ring-1 ring-slate-900/5 overflow-hidden">
                  <img src={LOGO} alt="" className="w-full h-full object-cover" />
                </span>
                <span className="font-heading font-bold text-lg text-ink">Reading Keeper</span>
              </div>

              <div className="flex-1 overflow-y-auto py-3">
                {links.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-${l.testid}`}
                      className={`flex items-center gap-3 px-6 py-3.5 font-body font-semibold text-lg active:bg-brand-sky/10 ${l.admin ? "text-brand-grape" : "text-ink"}`}
                    >
                      <l.icon size={24} weight="duotone" className={l.admin ? "text-brand-grape" : "text-brand-sky"} /> {l.label}
                    </Link>
                  </SheetClose>
                ))}
              </div>

              <div className="border-t border-slate-900/5 p-6">
                {user ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-brand-sky/40" />
                      ) : (
                        <UserCircle size={40} weight="duotone" className="text-brand-sky" />
                      )}
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-ink truncate">{user.name}</p>
                        <p className="font-body text-sm text-ink-soft truncate">{user.email}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => { setOpen(false); logout(); }}
                      variant="outline"
                      className="w-full rounded-full font-semibold border-2 border-slate-900/10"
                      data-testid="mobile-logout-btn"
                    >
                      <SignOut size={20} weight="duotone" className="mr-2" /> Log out
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => { setOpen(false); navigate("/login"); }}
                    className="w-full rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold py-6 text-lg"
                    data-testid="mobile-login-btn"
                  >
                    Sign in
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
